import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import type {ICommandBarItemProps} from "@fluentui/react";

import useIssacToastHook from "../../../../hooks/useIssacToastHook";
import useConstructionCommandBarItems from "./useConstructionCommandBarItems";
import IsaacToastWrapperType from "../../../../types/IsaacToastWrapperType";
import PathConstants from "../../../../routes/pathConstants";
import {addToLocaLStorage} from "../../../../utils/ManageLocalStorage";
import type {RootState} from "../../../../store";
import type ConstructionStatus from "../../../../types/ConstructionStatus";
import type {ModifiedConstructionNewElements} from "../../../../types/ModifiedConstructionNewElements";
import useCalculationNewHook from "../../../../hooks/useCalculationNewHook";

interface UseConstructionCommandBarReturn {
  items: ICommandBarItemProps[];
}

const useConstructionCommandBar = (
  constructionStatus: ConstructionStatus | undefined,
  setIsDeleteDialogOpen: () => void,
): UseConstructionCommandBarReturn => {
  const {id} = useParams();
  const {createStartCalculation, getLatestCalculationByConstructionId} = useCalculationNewHook();
  const {withToast} = useIssacToastHook();
  const navigate = useNavigate();

  const {editedProperties, deselectedEntities} = useSelector((state: RootState) => state.constructionNew);

  const newCalculation = async (constructionId: string, correlationId: string): Promise<void> => {
    const collection: ModifiedConstructionNewElements = {
      editedConstructionProperties: editedProperties ?? [],
      deselectedConstructionElements: deselectedEntities ?? [],
    };

    addToLocaLStorage(`${constructionId}.inProgress`, true);
    await withToast<string>(
      async () => await createStartCalculation(constructionId, correlationId, collection),
      IsaacToastWrapperType.Full,
      undefined,
      `Validating edited construction geometry and start calculation...`,
      `Edited construction geometry is valid.`,
      `Failed to validate edited construction geometry!`,
    );
  };

  const handleNewCalculationTrigger = async (): Promise<void> => {
    await newCalculation(id ?? "", uuidv4());
  };

  const handleConstructionDeleteTrigger = (): void => {
    setIsDeleteDialogOpen();
  };

  const handleCalculationNavigationTrigger = (): void => {
    void (async () => {
      if (id) {
        const latestCalculation = await getLatestCalculationByConstructionId(id);
        if (latestCalculation.data) {
          void navigate(`${PathConstants.Calculations}/${latestCalculation?.data?.calculationId}`);
        }
      }
    })();
  };

  const items = useConstructionCommandBarItems(
    constructionStatus,
    handleConstructionDeleteTrigger,
    handleNewCalculationTrigger,
    handleCalculationNavigationTrigger,
  );

  return {items};
};

export default useConstructionCommandBar;
